// SaveQuoteModal.jsx — standalone component
// Import and use in App.jsx when user clicks "Save to Log"

import React, { useState, useEffect, useRef } from "react";
import { useUser } from "@clerk/react";
import AdminBccToggles, { adminBccList } from "./AdminBccToggles";
import NoAutofillInput from "./NoAutofillInput";
import { resolveDiscount } from "./generateQuoteDocument";
import { presetPersonalNote } from "./discountPresets";
import { fetchAllQuotes, setTestQuote as setTestQuoteFlag, TEST_QUOTE_SEND_BLOCKED, appendQuote, appendAmendment, newSaveId, saveTestMode, ensureHeaders, STATUSES, sendQuoteEmail, recordClientSend, isInternalOnlySend, getQuoteContactName, AUTO_EMAIL_BLOCKS, defaultAutoEmailBlocks, getQuoteVehicleMake } from "./quoteLogger";

const modalInputStyle = {
  width: "100%", padding: "10px 12px", borderRadius: 6,
  border: "1px solid #d0d8e8", background: "#fff",
  color: "#1a1a2e", fontSize: 13, boxSizing: "border-box",
  WebkitTextFillColor: "#1a1a2e",
};
const modalTextareaStyle = {...modalInputStyle, minHeight: 80, resize: "vertical"};
const modalSelectStyle = {...modalInputStyle, cursor: "pointer"};

export default function SaveQuoteModal({
  quoteNumber,
  category,
  totalExVat,
  totalIncVat,
  margin,
  mileage,
  mileageCost,
  scopeText,
  application,
  amendmentOf,
  amendStatus,
  savedAs = "",  // already saved under this number and unchanged since: nothing new to write
  initialTestQuote = false, // the quote is a test (kept out of dashboard figures)
  initialClientName,
  initialClientEmail,
  initialClientPhone,
  initialSiteAddress,
  showSiteAddress = true, // false for Automotive in-unit jobs - no site to record
  stateJson,     // full calculator state for amendments
  onClose,
  onSaved,
}) {
  const [clientName, setClientName] = useState(initialClientName || "");
  const { user } = useUser();
  const [clientEmail, setClientEmail] = useState(initialClientEmail || "");
  // Who this email goes to - separate from the client email saved on the
  // quote, so a test send (or a send to someone else) never changes the
  // quote's own client record. Follows Client Email until edited.
  const [sendTo, setSendTo] = useState(initialClientEmail || "");
  const [sendToEdited, setSendToEdited] = useState(false);
  const [clientPhone, setClientPhone] = useState(initialClientPhone || "");
  const [siteAddress, setSiteAddress] = useState(initialSiteAddress || "");
  const [notes, setNotes] = useState("");
  // A discount preset (e.g. Showcase Project) pre-fills this; still editable.
  const [personalNote, setPersonalNote] = useState(() => presetPersonalNote(stateJson, resolveDiscount(stateJson?.commercial, Number(totalExVat) || 0)));
  const isAutoQuote = category === "Automotive";
  const vehicleMake = getQuoteVehicleMake(stateJson);
  const [emailBlocks, setEmailBlocks] = useState(() => defaultAutoEmailBlocks(stateJson));
  // Quote emails: BCC Edward by default, James opt-in.
  const [bcc, setBcc] = useState({ edward: true, james: false });
  // A new version starts as Draft until it's actually sent (Save & Email or
  // Send to Client moves it to Sent); the quote it amends keeps its status.
  const [status, setStatus] = useState(amendmentOf ? (amendStatus || "Draft") : "Draft");
  const [testQuote, setTestQuote] = useState(!!initialTestQuote);
  // Unchanged quote: nothing new is written, so show the status it actually
  // has in the Quote Log (read-only) rather than the default for a new save.
  const [savedStatus, setSavedStatus] = useState(savedAs ? "…" : "");
  useEffect(() => {
    if (!savedAs) return;
    let cancelled = false;
    fetchAllQuotes()
      .then(rows => { if (!cancelled) setSavedStatus(rows.find(r => r["Quote Number"] === savedAs)?.["Status"] || "Draft"); })
      .catch(() => { if (!cancelled) setSavedStatus("Unknown"); });
    return () => { cancelled = true; };
  }, [savedAs]);
  const [loading, setLoading] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [finishing, setFinishing] = useState(false); // email accepted, Quote Log updates still running
  const [savedSuccess, setSavedSuccess] = useState(false);
  const [error, setError] = useState("");
  const [renumberedTo, setRenumberedTo] = useState(null);

  // One save per dialog. busyRef blocks a second click the instant the first
  // lands (state alone can lag a fast double click); savedDataRef means any
  // later save request in this dialog returns the row already written
  // instead of creating another quote number.
  const busyRef = useRef(false);
  const savedDataRef = useRef(null);
  // One save ID for this dialog: a retry after a slow or failed save reuses
  // it, so it can never add a second row (see writeWithSaveCheck).
  const saveIdRef = useRef(newSaveId());
  const [checking, setChecking] = useState(false); // "Still saving… checking whether it went through"
  const [notSaved, setNotSaved] = useState(""); // set when a save is confirmed not to have gone through
  const [testMode] = useState(() => saveTestMode());
  const saveBannerRef = useRef(null);
  useEffect(() => { if ((checking || notSaved) && saveBannerRef.current) saveBannerRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" }); }, [checking, notSaved]);
  const [busy, setBusy] = useState(false);
  const runOnce = async (fn) => {
    if (busyRef.current) return;
    busyRef.current = true; setBusy(true);
    let ok = false;
    try { ok = await fn(); } finally {
      // Stay locked after a successful save (the dialog is about to close);
      // unlock after a failure so it can be retried.
      if (!ok) { busyRef.current = false; setBusy(false); }
    }
  };
  // Closing after a save still reports it, so the calculator marks the quote
  // as saved (and won't number it again).
  // The save is reported to the calculator exactly once, however the dialog
  // ends (button, Save & Email's pause, or closing it).
  const reportedRef = useRef(false);
  const report = (data) => { if (reportedRef.current) return; reportedRef.current = true; if (onSaved) onSaved(data); };
  const close = () => {
    if (savedDataRef.current) { report(savedDataRef.current); return; }
    if (busyRef.current) return; // mid-save: wait for it to finish
    onClose();
  };

  async function handleSave() {
    if (savedDataRef.current) return savedDataRef.current;
    if (!quoteNumber) {
      setError("No quote number — generate a quote first.");
      return;
    }
    if (savedAs) {
      // Nothing has changed since it was saved, so no new row: hand back the
      // existing one (fresh from the sheet, so a send sees its real status).
      setLoading(true);
      setError("");
      try {
        const row = (await fetchAllQuotes()).find(r => r["Quote Number"] === savedAs);
        // The test tickbox is the one thing that can change here.
        if (row?._rowIndex && testQuote !== (row["Test Quote"] === "Yes")) await setTestQuoteFlag(row._rowIndex, testQuote);
        setLoading(false);
        return { quoteNumber: savedAs, rowIndex: row?._rowIndex, status: row?.["Status"] || "Draft", unchanged: true, testQuote };
      } catch (e) {
        setError(e.message);
        setLoading(false);
        return;
      }
    }
    setLoading(true);
    setError("");
    setNotSaved("");
    setRenumberedTo(null);
    try {
      await ensureHeaders();
      const data = {
        quoteNumber,
        dateCreated: new Date().toLocaleDateString("en-GB"),
        clientName: clientName.trim(),
        clientEmail: clientEmail.trim(),
        clientPhone: clientPhone.trim(),
        category,
        application,
        siteAddress: showSiteAddress ? siteAddress.trim() : "",
        status,
        totalExVat,
        totalIncVat,
        margin,
        mileage,
        mileageCost,
        notes: notes.trim(),
        amendmentOf: amendmentOf || "",
        testQuote,
        pdfGenerated: false,
        emailSent: false,
        scopeSummary: scopeText || "",
        stateJson: stateJson || null,
        saveId: saveIdRef.current,
      };
      const saveOpts = { onSlow: () => setChecking(true) };
      if (amendmentOf) {
        // quoteNumber here is only a provisional display value - the Apps
        // Script assigns the real one from the sheet's live state, locked, to
        // rule out two amendments started close together colliding on the
        // same letter. Use whatever it actually assigned from here on.
        const result = await appendAmendment(data, saveOpts);
        if (result.quoteNumber && result.quoteNumber !== data.quoteNumber) {
          setRenumberedTo(result.quoteNumber);
        }
        data.quoteNumber = result.quoteNumber || data.quoteNumber;
        data.rowIndex = result.rowIndex;
      } else {
        // New quotes are numbered by the Apps Script too (see appendQuote).
        const result = await appendQuote(data, saveOpts);
        if (result.quoteNumber && result.quoteNumber !== data.quoteNumber) {
          setRenumberedTo(result.quoteNumber);
        }
        data.quoteNumber = result.quoteNumber || data.quoteNumber;
        data.rowIndex = result.rowIndex;
      }
      savedDataRef.current = data;
      setLoading(false);
      setChecking(false);
      return data;
    } catch (e) {
      if (e.notSaved) setNotSaved(e.message.replace(/^Not saved, try again\.\s*/, "") || "No reply from the Quote Log");
      else setError(e.message);
      setLoading(false);
      setChecking(false);
    }
  }

  function handleSaveAndEmail() { return runOnce(saveAndEmail); }
  async function saveAndEmail() {
    if (testQuote && sendTo.trim() && !isInternalOnlySend(sendTo)) { setError(TEST_QUOTE_SEND_BLOCKED); return false; }
    // Re-sending a quote that's already saved: confirm first, so a real
    // client isn't emailed twice by accident (test sends to us don't ask).
    if (savedAs && sendTo.trim() && !isInternalOnlySend(sendTo) && !window.confirm(`Send ${savedAs} to ${sendTo.trim()} again?`)) return false;
    const data = await handleSave();
    if (!data) return false;
    // The Quote Log opens on this quote afterwards, saying where it went.
    data.sendNote = await handleSendEmail(data.quoteNumber, data);
    setSavedSuccess(true);
    setTimeout(() => report(data), 1200);
    return true;
  }
  // finalNumber: the number the quote was actually saved under.
  // saved: the row just written (rowIndex + status), so a client send can
  // move it to Sent. Returns a one-line note on what happened, for the
  // Quote Log.
  async function handleSendEmail(finalNumber = quoteNumber, saved = null) {
    if (!sendTo.trim()) { alert("Enter an address to send the quote to."); return "Not emailed: no address entered"; }
    let note = "";
    setEmailSending(true);
    try {
      await sendQuoteEmail({
        to: sendTo.trim(),
        quoteNumber: finalNumber,
        clientName: clientName.trim(),
        contactName: getQuoteContactName(stateJson),
        personalNote,
        blocks: emailBlocks,
        vehicleMake,
        bcc: adminBccList(bcc),
        category,
        totalIncVat: totalIncVat ? "£" + Number(totalIncVat).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—",
        totalExVat: totalExVat != null ? "£" + Number(totalExVat).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "",
        discount: resolveDiscount(stateJson?.commercial, Number(totalExVat) || 0),
        scopeSummary: scopeText || "",
        // Recipient history lives in SendLog, never on the quote row; the
        // server writes it once the email is accepted.
        log: { method: "Save & Email", sentBy: user?.primaryEmailAddress?.emailAddress || user?.fullName || "" },
      });
      // Email accepted; the Quote Log updates below can take a few seconds,
      // so "Saved & Sent!" waits for them.
      setFinishing(true);
      note = isInternalOnlySend(sendTo) ? (testQuote ? `Test send to ${sendTo.trim()}, marked as sent` : "Internal send, status unchanged") : `Sent to ${sendTo.trim()}`;
      // Awaited so the Quote Log, which opens next, already shows Sent.
      if (saved?.rowIndex) {
        try {
          await recordClientSend({
            rowIndex: saved.rowIndex, quoteNumber: finalNumber, currentStatus: saved.status, to: sendTo.trim(),
            changedBy: user?.primaryEmailAddress?.emailAddress || user?.fullName || "", method: "Save & Email",
            testQuote,
          });
        } catch (err) { console.error("Failed to mark quote as sent:", err); }
      }
      setEmailSent(true);
    } catch(e) {
      alert("Email failed: " + e.message);
      note = `Saved, but the email failed: ${e.message}`;
    }
    setEmailSending(false);
    return note;
  }

  const inputStyle = {
    width: "100%",
    padding: "9px 12px",
    borderRadius: 6,
    border: "1px solid #d0d8e8",
    fontSize: 13,
    boxSizing: "border-box",
    marginTop: 4,
    fontFamily: "'IBM Plex Sans', sans-serif",
    outline: "none",
    color: "#1a1a2e",
    background: "#fff",
    WebkitTextFillColor: "#1a1a2e",
    WebkitBoxShadow: "0 0 0px 1000px #ffffff inset",
  };

  const labelStyle = {
    fontSize: 11,
    color: "#666",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    fontWeight: 600,
    display: "block",
    marginTop: 2,
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(10,20,50,0.45)",
        zIndex: 1100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
      onClick={e => e.target === e.currentTarget && !busy && close()}
    >
      {/* The calculators' focus rule (App.jsx, .sp-calc input:focus) fills a
          focused field with the page background, which is dark navy in dark
          mode, so the dialog's dark text vanished while typing. This dialog is
          always white, so its focused fields stay white with dark text. */}
      <style>{`.sp-calc .sp-save-modal input:not([type=checkbox]):focus, .sp-calc .sp-save-modal select:focus, .sp-calc .sp-save-modal textarea:focus { border-color: #3a52cc !important; box-shadow: 0 0 0 1px #3a52cc, 0 0 0 1000px #fff inset !important; color: #1a1a2e; -webkit-text-fill-color: #1a1a2e; }`}</style>
      <div
        className="sp-save-modal"
        style={{
          background: "#fff",
          borderRadius: 12,
          padding: 28,
          width: "100%",
          maxWidth: 460,
          maxHeight: "90vh",
          overflowY: "auto",
          fontFamily: "'IBM Plex Sans', sans-serif",
          position: "relative",
          boxShadow: "0 12px 48px rgba(0,0,0,0.22)",
        }}
      >
        {/* Close */}
        <button
          onClick={close}
          style={{
            position: "absolute", top: 14, right: 14,
            background: "none", border: "none", fontSize: 18,
            cursor: "pointer", color: "#999", lineHeight: 1, padding: 4,
          }}
        >
          ✕
        </button>

        {/* Title */}
        <h3 style={{ margin: "0 0 6px", fontSize: 19, color: "#1D3461", fontWeight: 700 }}>
          Save Quote to Log
        </h3>

        {/* Summary line */}
        <div style={{
          fontSize: 13, color: "#555", marginBottom: 20,
          padding: "8px 12px", background: "#f0f4ff", borderRadius: 6,
          display: "flex", gap: 12, flexWrap: "wrap",
        }}>
          {/* New quotes have no number until saved (the Apps Script assigns
              it), so they're labelled "New quote"; amendments show theirs. */}
          <span>
            <span style={{ fontWeight: 700, color: "#1D3461" }}>{savedAs || (amendmentOf ? quoteNumber : "New quote")}</span>
            {category && <> · {category}</>}
            {totalExVat != null && <> · <strong>£{Number(totalExVat).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ex VAT</strong></>}
          </span>
          {application && <span style={{ color: "#8a95a3" }}>{application}</span>}
        </div>

        {testMode && !savedAs && (
          <div style={{ fontSize: 12, color: "#5B1A8A", background: "#F3E5FF", padding: "6px 12px", borderRadius: 6, marginBottom: 12, border: "1px dashed #9C4DCC" }}>
            Test switch on: <strong>{testMode}</strong> (previews only). Saves will act as if the reply never arrived.
          </div>
        )}

        {savedAs ? (
          <div style={{
            fontSize: 12, color: "#1D3461", background: "#EEF3FF",
            padding: "6px 12px", borderRadius: 6, marginBottom: 16,
            border: "1px solid #B9C8F0",
          }}>
            Already saved as <strong>{savedAs}</strong> and unchanged, so nothing new is saved. Save &amp; Email sends {savedAs}.
          </div>
        ) : amendmentOf && (
          <div style={{
            fontSize: 12, color: "#5B1A8A", background: "#F3E5FF",
            padding: "6px 12px", borderRadius: 6, marginBottom: 16,
            border: "1px solid #9C4DCC",
          }}>
            ✎ Amendment of <strong>{amendmentOf}</strong>. Saving creates <strong>{quoteNumber}</strong>
          </div>
        )}

        {renumberedTo && (
          <div style={{
            fontSize: 12, color: "#7B5800", background: "#FFF7E0",
            padding: "6px 12px", borderRadius: 6, marginBottom: 14,
            border: "1px solid #F5A623",
          }}>
            Saved as <strong>{renumberedTo}</strong>.
          </div>
        )}

        {error && (
          <div style={{
            padding: "10px 12px", background: "#FEECEC", color: "#8B1A1A",
            borderRadius: 6, marginBottom: 14, fontSize: 13,
            border: "1px solid #E53935",
          }}>
            {error}
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

          {/* Test quote: kept out of the dashboard figures and Needs attention */}
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#1a1a2e", cursor: "pointer" }}>
            <input type="checkbox" checked={testQuote} onChange={e => {
              // Unticking makes it a real quote that can go to clients.
              if (!e.target.checked && !window.confirm("This will make it a real quote and allow sending to clients. Continue?")) return;
              setTestQuote(e.target.checked);
            }} style={{ width: 16, height: 16 }} />
            Test quote <span style={{ fontSize: 11, color: "#8a95a3" }}>(not counted on the dashboard)</span>
          </label>

          {/* Status */}
          <div>
            <label style={labelStyle}>Status</label>
            {savedAs ? (
              <input style={{ ...inputStyle, color: "#4a5568", WebkitTextFillColor: "#4a5568", background: "#f4f6fa" }} value={savedStatus} readOnly title="Current status in the Quote Log. Nothing new is saved, so it isn't changed here." />
            ) : (
              <select
                value={status}
                onChange={e => setStatus(e.target.value)}
                style={{ ...inputStyle, cursor: "pointer" }}
              >
                {/* Lost is set from the Quote Log (Mark lost), which asks why. */}
                {STATUSES.filter(s => s !== "Lost").map(s => <option key={s}>{s}</option>)}
              </select>
            )}
          </div>

          {/* Client */}
          <div>
            <label style={labelStyle}>Client Name</label>
            <NoAutofillInput
              style={inputStyle}
              name="sp-clientname" value={clientName}
              onChange={e => setClientName(e.target.value)}
              placeholder="e.g. Acme Engineering Ltd"
            />
          </div>

          {/* Email */}
          <div>
            <label style={labelStyle}>Client Email</label>
            <NoAutofillInput
              style={inputStyle}
              type="email"
              name="sp-clientemail" value={clientEmail}
              onChange={e => { setClientEmail(e.target.value); if (!sendToEdited) setSendTo(e.target.value); }}
              placeholder="Type the client's email" className="sp-hint"
            />
          </div>

          {/* Recipient for Save & Email only - never saved over the client email */}
          <div>
            <label style={labelStyle}>Send Quote To</label>
            <NoAutofillInput
              style={inputStyle}
              type="email"
              name="sp-sendto" value={sendTo}
              onChange={e => { setSendTo(e.target.value); setSendToEdited(true); }}
              placeholder="Type the client's email" className="sp-hint"
            />
            <div style={{ fontSize: 11, color: "#8a95a3", marginTop: 4 }}>
              Only used by Save &amp; Email. The Client Email above is what's saved on the quote.
            </div>
            <div style={{ marginTop: 8 }}>
              <AdminBccToggles compact value={bcc} onChange={setBcc} disabled={emailSending} />
            </div>
          </div>

          {/* Phone */}
          <div>
            <label style={labelStyle}>Client Phone</label>
            <NoAutofillInput
              style={inputStyle}
              type="tel"
              name="sp-clientphone" value={clientPhone}
              onChange={e => setClientPhone(e.target.value)}
              placeholder="07700 900000"
            />
          </div>

          {/* Site - not shown for Automotive in-unit jobs */}
          {showSiteAddress && (
            <div>
              <label style={labelStyle}>Site Address</label>
              <NoAutofillInput
                style={inputStyle}
                name="sp-siteaddress" value={siteAddress}
                onChange={e => setSiteAddress(e.target.value)}
                placeholder="Works address or postcode"
              />
            </div>
          )}

          {/* Notes */}
          <div>
            <label style={labelStyle}>Internal Notes</label>
            <textarea
              style={{ ...inputStyle, minHeight: 68, resize: "vertical" }}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Any internal notes about this quote…"
            />
          </div>

          {/* Custom email paragraph - email only */}
          <div>
            <label style={labelStyle}>Custom email paragraph (optional)</label>
            <textarea
              style={{ ...inputStyle, minHeight: 68, resize: "vertical" }}
              value={personalNote}
              onChange={e => setPersonalNote(e.target.value)}
            />
            <div style={{ fontSize: 11, color: "#8a95a3", marginTop: 4 }}>
              Your own wording for this client, added between the greeting and the quote summary. Only used by Save &amp; Email.
            </div>
          </div>

          {/* Optional Automotive email blocks - only ticked ones are sent. */}
          {isAutoQuote && (
            <div>
              <label style={labelStyle}>Include in Email</label>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {AUTO_EMAIL_BLOCKS.map(b => (
                  <label key={b.key} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#1a1a2e", cursor: "pointer" }}>
                    <input type="checkbox" checked={!!emailBlocks[b.key]} onChange={e => setEmailBlocks(v => ({ ...v, [b.key]: e.target.checked }))} />
                    {b.label}
                  </label>
                ))}
              </div>
            </div>
          )}

        </div>

        {savedSuccess && (
          <div style={{ background: "#E6F9F0", border: "1px solid #27AE60", borderRadius: 8, padding: "14px 18px", marginTop: 16, textAlign: "center" }}>
            <div style={{ fontSize: 20, marginBottom: 4 }}>✅</div>
            <div style={{ fontWeight: 700, color: "#0D5C36", fontSize: 14 }}>Saved to log &amp; email sent!</div>
            <div style={{ color: "#0D5C36", fontSize: 12, marginTop: 4 }}>Closing in a moment...</div>
          </div>
        )}
        {/* Save in doubt: big and right above the buttons, so it can't be missed. */}
        <div ref={saveBannerRef}>
          {loading && checking && (
            <div role="status" style={{ background: "#FFF3D6", border: "2px solid #F5A623", borderRadius: 10, padding: "16px 18px", marginTop: 16, display: "flex", gap: 12, alignItems: "flex-start" }}>
              <div style={{ fontSize: 24, lineHeight: 1 }}>⏳</div>
              <div>
                <div style={{ fontWeight: 800, color: "#7B5800", fontSize: 16 }}>Still saving… checking whether it went through</div>
                <div style={{ color: "#7B5800", fontSize: 13, marginTop: 4 }}>The Quote Log is slow to reply. Don't close this or press Save again; this can take up to half a minute.</div>
              </div>
            </div>
          )}
          {notSaved && !loading && (
            <div role="alert" style={{ background: "#FEECEC", border: "2px solid #E53935", borderRadius: 10, padding: "16px 18px", marginTop: 16, display: "flex", gap: 12, alignItems: "flex-start" }}>
              <div style={{ fontSize: 24, lineHeight: 1 }}>⚠️</div>
              <div>
                <div style={{ fontWeight: 800, color: "#8B1A1A", fontSize: 16 }}>Not saved, try again</div>
                <div style={{ color: "#8B1A1A", fontSize: 13, marginTop: 4 }}>Nothing was added to the Quote Log. Press Save again; it can't create a duplicate.</div>
                {notSaved && <div style={{ color: "#a05050", fontSize: 11, marginTop: 6 }}>{notSaved}</div>}
              </div>
            </div>
          )}
        </div>
        {/* Buttons */}
        <div style={{ display: "flex", gap: 10, marginTop: 22 }}>
          <button
            onClick={close}
            style={{
              flex: 1, padding: "11px", borderRadius: 6,
              border: "1px solid #d0d8e8", background: "#fff",
              color: "#666", fontSize: 13, cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            Cancel
          </button>
          <button
            onClick={() => runOnce(async () => { const data = await handleSave(); if (data) report(data); return !!data; })}
            disabled={busy || loading}
            style={{
              flex: 2, padding: "11px", borderRadius: 6, border: "none",
              background: busy || loading ? "#8fa3c4" : "#1D3461",
              color: "#fff", fontSize: 13, fontWeight: 700,
              cursor: busy || loading ? "default" : "pointer",
              fontFamily: "inherit",
              transition: "background 0.2s",
            }}
          >
            {loading ? (savedAs ? "Opening…" : "Saving…") : savedAs ? "Open in Quote Log" : "💾 Save"}
          </button>
          <button
            onClick={handleSaveAndEmail}
            disabled={busy || emailSending || emailSent}
            style={{
              flex: 2, padding: "11px", borderRadius: 6, border: "none",
              background: emailSent ? "#0D5C36" : (busy || emailSending) ? "#8fa3c4" : "#3a52cc",
              color: "#fff", fontSize: 13, fontWeight: 700,
              cursor: busy || emailSending || emailSent ? "default" : "pointer",
              fontFamily: "inherit",
            }}
          >
            {emailSent ? "✅ Saved & Sent!" : finishing ? "Sent · updating the Quote Log…" : (busy || emailSending) ? "Saving & Sending…" : "📧 Save & Email to Client"}
          </button>
        </div>
      </div>
    </div>
  );
}
